import dayjs from 'dayjs'
import { decamelizeKeys } from 'humps'
import { stringify } from 'querystring'
import {
  AppointmentRoom,
  FeedbackRoom,
  Room,
  RoomUniqueId,
} from 'src/models/Room'
import { filterFalsy } from 'src/utils/array'
import { filterUndefined } from 'src/utils/object'
import { IterableElement } from 'type-fest'
import {
  AppointmentRoomResponse,
  FeedbackRoomResponse,
  FilterCondition,
  GroupRoomResponse,
  Opponent,
  RoomResponse,
  RoomsResponse,
} from '../types/room'

const isGroup = (uniqueId: (RoomResponse | GroupRoomResponse)['uniqueId']) =>
  uniqueId.includes('Group')
const toQueryableUniqueId = (
  uniqueId: (RoomResponse | GroupRoomResponse)['uniqueId'],
): RoomUniqueId => {
  const [_, id] = uniqueId.split('-')
  return isGroup(uniqueId) ? `g-${+id}` : `r-${+id}`
}

const toRoom = (
  data: RoomResponse | GroupRoomResponse,
  userId: number,
  ...counters: (Opponent | null | undefined)[]
): Room => ({
  id: data.id,
  uniqueId: toQueryableUniqueId(data.uniqueId),
  url: data.url,
  counters: filterFalsy(counters)
    .filter((counter) => counter.id !== userId)
    .map((counter) => ({
      id: counter.id,
      name: counter.adminName,
      nameFurigana: counter.adminNameFurigana || '',
      iconSrc: counter.image?.url || '',
      companyName: counter.companyName,
      position: counter.position || '',
      isReferrer: counter.isReferrer,
      role: counter.role,
      isAdminRole: counter.isAdminRole,
      isDeletedUser: counter.isDeleted || false,
      messageAvailable: counter.messageAvailable || false,
      isPaidPlan: counter.isPaidPlan || false,
      feedbackScore: counter.feedbackScore,
      feedbackCount: counter.feedbackCount,
    })),
  latestMessage: data.lastMessage
    ? {
        content: data.lastMessage.content,
        sentAt: dayjs(data.updatedAt).format('MM/DD'),
      }
    : undefined,
  hasDeletedUser:
    filterFalsy(counters).filter((c) => c.isDeleted === true).length !== 0,
  hasRead: !data.notRead,
  isRequestReceived: data.isRequestReceived,
  isHidden: data.isHidden,
  hiddenUrl: data.hiddenUrl,
  unhiddenUrl: data.unhiddenUrl,
  isGroup: isGroup(data.uniqueId),
  type: data.messageType,
  firstMessageId: data.firstOpponentMessageId,
  hasMemos: data.hasMemos,
  triggerText: {
    receivedGuideMessageFlag: data.triggerText?.receivedGuideMessageFlag,
    sendGuideMessageFlag: data.triggerText?.sendGuideMessageFlag,
    matchingDateText: data.triggerText?.matchingDateText,
    matchingDetailText: data.triggerText?.matchingDetailText,
  },
  new: !!data.new,
  pinned: data.pinned,
  unreadBatchMessageRoom: data.unreadBatchMessageRoom,
  customStatusId: data.customStatusId,
  createdAt: dayjs(data.createdAt),
  memoCount: data.memoCount,
  createType: data.createType,
  feedbacked: data.feedbacked,
  canFeedback: data.canFeedback,
  firstMessageSentAt: dayjs(data.firstMessageSentAt),
  tagMappings: data.tagMappings,
  urlForTimeline: data.urlForTimeline,
})

export const toRoomsQuery = (param: FilterCondition) => {
  const query = stringify(
    decamelizeKeys(
      filterUndefined({
        ...param,
        includeRequestReason: true, // TODO: remove when the feature, which being able to see requests with reason in a message box, on mobile is implemented.
        tag_ids: param.tagIds?.join(','), // FIXME: queryのarray型を使うようにする
      }),
    ) as any,
  )
  return query ? `?${query}` : ''
}

export const toRoomOrGroup = (
  room: RoomResponse | GroupRoomResponse,
  userId: number,
): Room =>
  room.messageType === 'CompanyMessageRoom'
    ? toRoom(room, userId, room.opponent)
    : toRoom(room, userId, room.reciever1, room.reciever2, room.sender)

export const toRooms = (data: RoomsResponse['rooms'], userId: number): Room[] =>
  data.map((room) => toRoomOrGroup(room, userId))

export const toFeedbackRoom = (
  data: IterableElement<FeedbackRoomResponse['roomsBeforeFeedback']>,
): FeedbackRoom => ({
  id: data.id,
  roomType: data.messageType,
  uniqueId: toQueryableUniqueId(data.uniqueId),
  firstMessageSentAt: dayjs(data.firstMessageSentAt),
  counter: {
    id: data.opponent.id,
    name: data.opponent.adminName,
    companyName: data.opponent.companyName,
    iconSrc: data.opponent.image?.url,
    position: data.opponent.position,
    isPaidPlan: data.opponent.isPaidPlan,
  },
})

export const toAppointmentRoom = (
  data: IterableElement<AppointmentRoomResponse['roomsBeforeAppointment']>,
): AppointmentRoom => ({
  id: data.id,
  roomType: data.messageType,
  uniqueId: toQueryableUniqueId(data.uniqueId),
  counter: {
    id: data.opponent.id,
    name: data.opponent.adminName,
    companyName: data.opponent.companyName,
    iconSrc: data.opponent.image,
    position: data.opponent.position,
    isPaidPlan: data.opponent.isPaidPlan,
  },
})
